'use client';

import React, { useEffect, useRef, useState } from 'react';

const EXAMPLES = [
  {
    instead: 'Which outlet performed best last month?',
    vina: 'Outlet 3 grew 18% and contributed the highest increase in revenue.',
    category: 'Sales',
  },
  {
    instead: 'What should I reorder?',
    vina: 'These 7 products are likely to run low based on recent sales.',
    category: 'Inventory',
  },
  {
    instead: 'Who still owes us?',
    vina: '12 customers have overdue payments above ₹50,000.',
    category: 'Finance',
  },
  {
    instead: 'What needs my attention today?',
    vina: 'Here are the 5 things that need your attention.',
    category: 'Operations',
  },
];

export default function MemoryToIntelligence() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="memory-to-intelligence"
      ref={sectionRef}
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #F6F5F0 0%, #EEF4F8 50%, #F6F5F0 100%)' }}
      aria-label="From memory to intelligence"
    >
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.1) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'clamp(2rem, 4.5vw, 3.2rem)',
              letterSpacing: '-0.03em',
              color: '#0D2620',
              lineHeight: 1.1,
              marginBottom: '16px',
            }}
          >
            From memory to intelligence.
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '1.05rem',
              color: '#3D5550',
              maxWidth: '440px',
              margin: '0 auto',
              lineHeight: 1.65,
            }}
          >
            Stop relying on who remembers what. VINA knows.
          </p>
        </div>

        {/* Examples */}
        <div className="flex flex-col gap-5">
          {EXAMPLES?.map((ex, i) => (
            <div
              key={i}
              className="rounded-3xl overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.8)',
                border: '1px solid rgba(211,223,219,0.7)',
                boxShadow: '0 4px 20px rgba(31,77,63,0.05)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.55s ease ${i * 100}ms, transform 0.55s cubic-bezier(0.22,1,0.36,1) ${i * 100}ms`,
              }}
            >
              <div className="grid md:grid-cols-2">
                {/* Instead of */}
                <div
                  className="p-6 md:p-8"
                  style={{ borderRight: '1px solid rgba(211,223,219,0.5)', borderBottom: '1px solid rgba(211,223,219,0.5)' }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className="px-2.5 py-1 rounded-full text-[10px] font-medium"
                      style={{
                        background: 'rgba(155,58,42,0.07)',
                        color: '#9B3A2A',
                        fontFamily: 'var(--font-mono)',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Instead of
                    </span>
                    <span
                      className="px-2.5 py-1 rounded-full text-[10px]"
                      style={{
                        background: 'rgba(211,223,219,0.4)',
                        color: '#7B8F88',
                        fontFamily: 'var(--font-mono)',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {ex?.category}
                    </span>
                  </div>
                  <p
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '15px',
                      color: '#5A3A35',
                      lineHeight: 1.6,
                      fontStyle: 'italic',
                    }}
                  >
                    &ldquo;{ex?.instead}&rdquo;
                  </p>
                </div>

                {/* VINA answers */}
                <div className="p-6 md:p-8" style={{ background: 'rgba(31,77,63,0.02)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className="px-2.5 py-1 rounded-full text-[10px] font-medium"
                      style={{
                        background: 'rgba(31,77,63,0.08)',
                        color: '#1F4D3F',
                        fontFamily: 'var(--font-mono)',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                      }}
                    >
                      VINA answers
                    </span>
                  </div>
                  <p
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '15px',
                      color: '#1A3530',
                      lineHeight: 1.6,
                      fontWeight: 500,
                    }}
                  >
                    {ex?.vina}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
